/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { CellPadding, CurrentRowVariant } from '#UNSAFE_TableView';
import type { CellStyles } from '../themes/TableCellStyles.css';
import type { FooterCellStyles } from '../themes/TableFooterCellStyles.css';
import type { HeaderCellStyles } from '../themes/TableHeaderCellStyles.css';

import { textAlignStyles, verticalAlignStyles } from '#PRIVATE_TableCommon/themes/Common.css';
import { tableCellStyles, tableCellStylesHC } from '../themes/TableCellStyles.css';
import {
  tableFooterCellStyles,
  tableFooterCellStylesHC
} from '../themes/TableFooterCellStyles.css';
import {
  tableHeaderCellStyles,
  tableHeaderCellStylesHC
} from '../themes/TableHeaderCellStyles.css';

type DataCellThemingProps = {
  isFirstColumnIndex: boolean;
  isFinalColumnIndex: boolean;
  isFirstRowIndex: boolean;
  isFinalRowIndex: boolean;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasLastHorizontalGridline: boolean;
  isRowSelected?: boolean;
  isPreviousRowSelected?: boolean;
  isColumnSelected?: boolean;
  isNextColumnSelected?: boolean;
  isPreviousColumnSelected?: boolean;
  isShowFocusRing?: boolean;
  isSticky: boolean;
  isRowSelectionEnabled?: boolean;
  isRowSingleSelection?: boolean;
  isActive?: boolean;
  isHover?: boolean;
  isPseudoHover?: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  horizontalAlignment: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment: 'top' | 'center' | 'bottom';
  variant?: CurrentRowVariant;
  padding?: CellPadding;
};

/**
 * Helper function to generate the class array for a data cell.
 */
export const getDataCellClassArray = ({
  isFirstColumnIndex,
  isFinalColumnIndex,
  isFirstRowIndex,
  isFinalRowIndex,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasLastHorizontalGridline,
  isRowSelected,
  isPreviousRowSelected,
  isColumnSelected,
  isNextColumnSelected,
  isPreviousColumnSelected,
  isShowFocusRing,
  isSticky,
  isRowSelectionEnabled,
  isRowSingleSelection,
  isActive,
  isHover,
  isPseudoHover,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isRtl,
  isHighContrast,
  horizontalAlignment,
  verticalAlignment,
  variant,
  padding
}: DataCellThemingProps) => {
  const cellStyles = isHighContrast ? tableCellStylesHC : tableCellStyles;
  const classArray = [cellStyles.base];

  // Padding styling logic
  const { isTopPadding, isBottomPadding, isStartPadding, isEndPadding } = _getPaddingInfo(padding);

  // Horizontal and vertical alignment styling logic
  _applyAlignmentStyling(classArray, horizontalAlignment, verticalAlignment);

  // Sticky styling logic
  _applyStickyStyling(classArray, cellStyles, isSticky, isStickyStartEdge, isStickyEndEdge, isRtl);

  // Interaction styling logic
  if (isRowSelectionEnabled) {
    if (isActive) {
      classArray.push(cellStyles.active);
    } else if (isHover) {
      classArray.push(cellStyles.hover);
    } else if (isPseudoHover) {
      classArray.push(cellStyles.pseudoHover);
    }
  }
  if (variant === 'highlight') {
    classArray.push(cellStyles.rowHighlight);
  }
  _applyFocusStyling(classArray, cellStyles, isShowFocusRing);

  // Selected Rows and Horizontal Gridline styling logic
  let hasBackground = false;
  if (isRowSelected) {
    hasBackground = true;
    classArray.push(cellStyles.selectedBackground);
  }
  if (isRowSingleSelection && (isRowSelected || isPreviousRowSelected)) {
    classArray.push(cellStyles.horizontalSelectedGridTop);
    classArray.push(
      isTopPadding ? cellStyles.selectedBorderTopPadding : cellStyles.selectedBorderTopNoPadding
    );
  } else if (!isFirstRowIndex && hasHorizontalGridlines) {
    classArray.push(cellStyles.horizontalGridTop);
    classArray.push(isTopPadding ? cellStyles.borderTopPadding : cellStyles.borderTopNoPadding);
  } else {
    classArray.push(isTopPadding ? cellStyles.topSpacerPadding : cellStyles.topSpacerNoPadding);
  }
  if (isFinalRowIndex) {
    classArray.push(cellStyles.lastRow);
    if (isRowSingleSelection && isRowSelected) {
      classArray.push(cellStyles.horizontalSelectedGridBottom);
      classArray.push(
        isBottomPadding
          ? cellStyles.selectedBorderBottomPadding
          : cellStyles.selectedBorderBottomNoPadding
      );
    } else if (hasLastHorizontalGridline) {
      classArray.push(cellStyles.horizontalGridBottom);
      classArray.push(
        isBottomPadding ? cellStyles.borderBottomPadding : cellStyles.borderBottomNoPadding
      );
    } else {
      classArray.push(
        isBottomPadding ? cellStyles.bottomSpacerPadding : cellStyles.bottomSpacerNoPadding
      );
    }
  } else {
    classArray.push(cellStyles.notLastRow);
    classArray.push(isBottomPadding ? cellStyles.bottomPadding : cellStyles.bottomNoPadding);
  }

  // Selected Columns and Vertical Gridline styling logic
  if (isColumnSelected) {
    if (!hasBackground) {
      classArray.push(cellStyles.selectedBackground);
      hasBackground = true;
    }
    _applySelectedColumnStyling(
      classArray,
      cellStyles,
      isStartPadding,
      isEndPadding,
      hasStartEdge,
      hasEndEdge
    );
  } else {
    if (!hasBackground) {
      classArray.push(cellStyles.background);
    }
    _applyColumnStyling(
      classArray,
      cellStyles,
      isStartPadding,
      isEndPadding,
      isFirstColumnIndex,
      isFinalColumnIndex,
      hasStartEdge,
      hasEndEdge,
      hasVerticalGridlines,
      isNextColumnSelected,
      isPreviousColumnSelected
    );
  }
  return classArray;
};

type HeaderFooterCellThemingProps = {
  isHeader: boolean;
  isActiveStyle?: boolean;
  isHoverStyle?: boolean;
  isPreviousCellActiveStyle?: boolean;
  isPreviousCellHoverStyle?: boolean;
  isFirstColumnIndex: boolean;
  isFinalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSelected?: boolean;
  isNextColumnSelected?: boolean;
  isPreviousColumnSelected?: boolean;
  isSticky: boolean;
  stickyInteractionIndex?: number;
  stickyNeighborIndex?: number;
  isShowFocusRing?: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isRtl: boolean;
  horizontalAlignment: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment: 'top' | 'center' | 'bottom';
  isHighContrast: boolean;
  padding?: CellPadding;
};

/**
 * Helper function to generate the class array for a header or footer cell.
 */
export const getHeaderFooterCellClassArray = ({
  isHeader,
  isActiveStyle,
  isHoverStyle,
  isPreviousCellActiveStyle,
  isPreviousCellHoverStyle,
  isFirstColumnIndex,
  isFinalColumnIndex,
  hasVerticalGridlines,
  isSelected,
  isNextColumnSelected,
  isPreviousColumnSelected,
  isSticky,
  isShowFocusRing,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isRtl,
  horizontalAlignment,
  verticalAlignment,
  isHighContrast,
  padding
}: HeaderFooterCellThemingProps) => {
  let cellStyles;

  if (isHeader) {
    cellStyles = isHighContrast ? tableHeaderCellStylesHC : tableHeaderCellStyles;
  } else {
    cellStyles = isHighContrast ? tableFooterCellStylesHC : tableFooterCellStyles;
  }
  const classArray = [cellStyles.base];

  // Padding styling logic
  const { isTopPadding, isBottomPadding, isStartPadding, isEndPadding } = _getPaddingInfo(padding);
  classArray.push(isBottomPadding ? cellStyles.bottomPadding : cellStyles.bottomNoPadding);
  classArray.push(isTopPadding ? cellStyles.topPadding : cellStyles.topNoPadding);

  // Horizontal and vertical alignment styling logic
  _applyAlignmentStyling(classArray, horizontalAlignment, verticalAlignment);

  // Sticky styling logic
  _applyStickyStyling(classArray, cellStyles, isSticky, isStickyStartEdge, isStickyEndEdge, isRtl);

  // Interaction styling logic
  _applyFocusStyling(classArray, cellStyles, isShowFocusRing);

  // Selected and Vertical Gridline styling logic
  if (isSelected) {
    if (isActiveStyle) {
      classArray.push(cellStyles.activeSelected);
    } else if (isHoverStyle) {
      classArray.push(cellStyles.hoverSelected);
    }
    classArray.push(cellStyles.selectedBackground);

    _applySelectedColumnStyling(
      classArray,
      cellStyles,
      isStartPadding,
      isEndPadding,
      hasStartEdge,
      hasEndEdge
    );
  } else {
    if (isActiveStyle) {
      classArray.push(cellStyles.active);
    } else if (isHoverStyle) {
      classArray.push(cellStyles.hover);
    }
    classArray.push(cellStyles.background);

    _applyColumnStyling(
      classArray,
      cellStyles,
      isStartPadding,
      isEndPadding,
      isFirstColumnIndex,
      isFinalColumnIndex,
      hasStartEdge,
      hasEndEdge,
      hasVerticalGridlines,
      isNextColumnSelected,
      isPreviousColumnSelected,
      isActiveStyle,
      isHoverStyle,
      isPreviousCellActiveStyle,
      isPreviousCellHoverStyle
    );
  }
  return classArray;
};

const _getPaddingInfo = (padding?: CellPadding) => {
  const isPaddingDisabled = padding === 'disabled';
  const isComplexPadding = padding instanceof Object;
  const isBottomPadding = !(
    isPaddingDisabled ||
    (isComplexPadding && padding?.bottom === 'disabled')
  );
  const isEndPadding = !(isPaddingDisabled || (isComplexPadding && padding?.end === 'disabled'));
  const isStartPadding = !(
    isPaddingDisabled ||
    (isComplexPadding && padding?.start === 'disabled')
  );
  const isTopPadding = !(isPaddingDisabled || (isComplexPadding && padding?.top === 'disabled'));
  return { isTopPadding, isBottomPadding, isStartPadding, isEndPadding };
};

const _applyAlignmentStyling = (
  classArray: string[],
  horizontalAlignment: 'start' | 'end' | 'left' | 'right' | 'center',
  verticalAlignment: 'top' | 'center' | 'bottom'
) => {
  classArray.push(textAlignStyles[horizontalAlignment]);
  classArray.push(verticalAlignStyles[verticalAlignment]);
};

const _applyFocusStyling = (
  classArray: string[],
  cellStyles: CellStyles | HeaderCellStyles | FooterCellStyles,
  isShowFocusRing?: boolean
) => {
  if (isShowFocusRing) {
    classArray.push(cellStyles.focused);
  }
};

const _applyStickyStyling = (
  classArray: string[],
  cellStyles: CellStyles | HeaderCellStyles | FooterCellStyles,
  isSticky: boolean,
  isStickyStartEdge: boolean,
  isStickyEndEdge: boolean,
  isRtl: boolean
) => {
  if (isSticky) {
    classArray.push(cellStyles.stickyColumn);
  }
  if (isStickyStartEdge) {
    classArray.push(cellStyles.stickyEdge);
    classArray.push(isRtl ? cellStyles.stickyEdgeLeft : cellStyles.stickyEdgeRight);
  } else if (isStickyEndEdge) {
    classArray.push(cellStyles.stickyEdge);
    classArray.push(isRtl ? cellStyles.stickyEdgeRight : cellStyles.stickyEdgeLeft);
  }
};

const _applyColumnStyling = (
  classArray: string[],
  cellStyles: CellStyles | HeaderCellStyles | FooterCellStyles,
  isStartPadding: boolean,
  isEndPadding: boolean,
  isFirstColumnIndex: boolean,
  isFinalColumnIndex: boolean,
  hasStartEdge: boolean,
  hasEndEdge: boolean,
  hasVerticalGridlines: boolean,
  isNextColumnSelected?: boolean,
  isPreviousColumnSelected?: boolean,
  isActiveStyle?: boolean,
  isHoverStyle?: boolean,
  isPreviousCellActiveStyle?: boolean,
  isPreviousCellHoverStyle?: boolean
) => {
  if (hasStartEdge) {
    if (isPreviousColumnSelected) {
      classArray.push(cellStyles.verticalSelectedGridStart);
      classArray.push(
        isStartPadding
          ? cellStyles.selectedBorderStartPadding
          : cellStyles.selectedBorderStartNoPadding
      );
    } else if (
      isActiveStyle ||
      isHoverStyle ||
      isPreviousCellActiveStyle ||
      isPreviousCellHoverStyle ||
      (!isFirstColumnIndex && hasVerticalGridlines)
    ) {
      classArray.push(cellStyles.verticalGridStart);
      classArray.push(
        isStartPadding ? cellStyles.borderStartPadding : cellStyles.borderStartNoPadding
      );
    } else if (isStartPadding) {
      classArray.push(cellStyles.startSpacerPadding);
    } else {
      classArray.push(cellStyles.startSpacerNoPadding);
    }
  } else if (isStartPadding) {
    classArray.push(cellStyles.startPadding);
  } else {
    classArray.push(cellStyles.startNoPadding);
  }

  if (hasEndEdge) {
    if (isNextColumnSelected) {
      classArray.push(cellStyles.verticalSelectedGridEnd);
      classArray.push(
        isEndPadding ? cellStyles.selectedBorderEndPadding : cellStyles.selectedBorderEndNoPadding
      );
    } else if (isActiveStyle || isHoverStyle || (!isFinalColumnIndex && hasVerticalGridlines)) {
      classArray.push(cellStyles.verticalGridEnd);
      classArray.push(isEndPadding ? cellStyles.borderEndPadding : cellStyles.borderEndNoPadding);
    } else if (isEndPadding) {
      classArray.push(cellStyles.endSpacerPadding);
    } else {
      classArray.push(cellStyles.endSpacerNoPadding);
    }
  } else if (isEndPadding) {
    classArray.push(cellStyles.endPadding);
  } else {
    classArray.push(cellStyles.endNoPadding);
  }
};

const _applySelectedColumnStyling = (
  classArray: string[],
  cellStyles: CellStyles | HeaderCellStyles | FooterCellStyles,
  isStartPadding: boolean,
  isEndPadding: boolean,
  hasStartEdge: boolean,
  hasEndEdge: boolean
) => {
  if (hasStartEdge) {
    classArray.push(cellStyles.verticalSelectedGridStart);
    classArray.push(
      isStartPadding
        ? cellStyles.selectedBorderStartPadding
        : cellStyles.selectedBorderStartNoPadding
    );
  } else {
    classArray.push(isStartPadding ? cellStyles.startPadding : cellStyles.startNoPadding);
  }
  if (hasEndEdge) {
    classArray.push(cellStyles.verticalSelectedGridEnd);
    classArray.push(
      isEndPadding ? cellStyles.selectedBorderEndPadding : cellStyles.selectedBorderEndNoPadding
    );
  } else {
    classArray.push(isEndPadding ? cellStyles.endPadding : cellStyles.endNoPadding);
  }
};

/**
 * Helper function to convert a column's horizontal alignment value into a form control's value.
 */
export const getFormTextAlign = (
  horizontalAlignment: 'start' | 'end' | 'left' | 'right' | 'center',
  isRtl: boolean
) => {
  switch (horizontalAlignment) {
    case 'left':
      return isRtl ? 'end' : 'start';
    case 'center':
      return undefined;
    default:
      return horizontalAlignment;
  }
};
